import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { useAuth } from "./auth/AuthProvider";
import { Layout } from "./components/Layout";
import { LoginPage } from "./pages/LoginPage";
import { SetupPage } from "./pages/SetupPage";
import { GuestPage } from "./pages/GuestPage";
import { WelcomePage } from "./pages/WelcomePage";
import { StudioConnectPage } from "./pages/StudioConnectPage";
import { PracticePage } from "./pages/PracticePage";
import { ClocksPage } from "./pages/ClocksPage";
import { RundownPage } from "./pages/RundownPage";
import { StatsPage } from "./pages/StatsPage";
import { AccountPage } from "./pages/AccountPage";
import { ReportsPage } from "./pages/ReportsPage";
import { NewsPage } from "./pages/NewsPage";
import { DashboardPage } from "./pages/DashboardPage";
import { BrandingPage } from "./pages/BrandingPage";
import { TeamPage } from "./pages/TeamPage";
import { ModulePage } from "./pages/ModulePage";
import { MitmachenPage } from "./pages/MitmachenPage";
import { PosteingangPage } from "./pages/PosteingangPage";
import { GewinnspielPage } from "./pages/GewinnspielPage";
import { SiteBuilderPage } from "./pages/SiteBuilderPage";
import { RedaktionPage } from "./pages/RedaktionPage";
import { ChannelsPage, ChannelDetailPage } from "./pages/ChannelsPage";
import { LibraryPage } from "./pages/LibraryPage";
import { PlayoutPage } from "./pages/PlayoutPage";
import { SchedulePage, ShowsPage, ModeratorsPage } from "./pages/SchedulePages";
import { PodcastPage } from "./pages/PodcastPage";
import { ServersPage } from "./pages/ServersPage";
import { EventsPage } from "./pages/EventsPage";
import { LoadingScreen } from "./components/LoadingScreen";
import { FrageDerWochePage } from "./pages/FrageDerWochePage";
import { PhonePage } from "./pages/PhonePage";
import { ChatPage } from "./pages/ChatPage";
import { MitschnittPage } from "./pages/MitschnittPage";
import { VeranstaltungPage } from "./pages/VeranstaltungPage";
import { PasswortNeuPage, PasswortVergessenPage } from "./pages/PasswortPages"; // f-auth
// Werbeplanung (f-werbung)
import { WerbungPage } from "./pages/WerbungPage";
// Kanalseiten: Hörerseite je Kanal (f-web)
import { KanalHoererseitePage } from "./pages/KanalHoererseitePage";
// Hosting: Betreiber-Konsole und „Mein Tarif“ (h-ui)
import { RadiosPage } from "./pages/betrieb/RadiosPage";
import { RadioDetailPage } from "./pages/betrieb/RadioDetailPage";
import { TarifePage } from "./pages/betrieb/TarifePage";
import { NutzungPage } from "./pages/betrieb/NutzungPage";
import { MeinTarifPage } from "./pages/MeinTarifPage";
import { EigeneAdressePage } from "./pages/EigeneAdressePage"; // h-domain
// h-umzug: Import, Löschnachweise, Datenauskunft
import { ImportPage } from "./pages/betrieb/ImportPage";
import { LoeschnachweisePage } from "./pages/betrieb/LoeschnachweisePage";
import { MeineDatenPage } from "./pages/MeineDatenPage";

function RadioDetailRoute(): JSX.Element {
  const { id = "" } = useParams<{ id: string }>();
  return <RadioDetailPage key={id} id={id} />;
}

function KanalHoererseiteRoute(): JSX.Element {
  const { slug = "" } = useParams<{ slug: string }>();
  return <KanalHoererseitePage slug={slug} />;
}

function RequireAuth({ children }: { children: JSX.Element }): JSX.Element {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

/** Anwärter landen direkt beim Probebetrieb – alles andere ist für sie ohnehin gesperrt. */
function HomeRoute(): JSX.Element {
  const { user } = useAuth();
  if (user?.role === "trainee") return <Navigate to="/practice" replace />;
  return <DashboardPage />;
}

function ChannelDetailRoute(): JSX.Element {
  const { slug = "" } = useParams<{ slug: string }>();
  return <ChannelDetailPage slug={slug} />;
}

function PlayoutRoute(): JSX.Element {
  return <PlayoutPage />;
}

function ScheduleRoute(): JSX.Element {
  return <SchedulePage />;
}

function ShowsRoute(): JSX.Element {
  return <ShowsPage />;
}

function ModeratorsRoute(): JSX.Element {
  return <ModeratorsPage />;
}

function PodcastRoute(): JSX.Element {
  return <PodcastPage />;
}

export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/setup" element={<SetupPage />} />
      <Route path="/gast/:token" element={<GuestPage />} />
      {/* f-auth: Passwort vergessen – öffentlich, ohne Shell */}
      <Route path="/passwort-vergessen" element={<PasswortVergessenPage />} />
      <Route path="/passwort-neu" element={<PasswortNeuPage />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route index element={<HomeRoute />} />
        <Route path="dashboard" element={<HomeRoute />} />
        <Route path="welcome" element={<WelcomePage />} />
        <Route path="studio" element={<StudioConnectPage />} />
        <Route path="practice" element={<PracticePage />} />
        <Route path="team" element={<TeamPage />} />
        <Route path="clocks" element={<ClocksPage />} />
        <Route path="stats" element={<StatsPage />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="news" element={<NewsPage />} />
        <Route path="module" element={<ModulePage />} />
        <Route path="mitmachen" element={<MitmachenPage />} />
        <Route path="posteingang" element={<PosteingangPage />} />
        <Route path="gewinnspiel" element={<GewinnspielPage />} />
        <Route path="channels/:slug/rundown" element={<RundownPage />} />
        <Route path="branding" element={<BrandingPage />} />
        <Route path="seiten" element={<SiteBuilderPage />} />
        <Route path="redaktion" element={<RedaktionPage />} />
        <Route path="library" element={<LibraryPage />} />
        <Route path="shows" element={<ShowsRoute />} />
        <Route path="moderators" element={<ModeratorsRoute />} />
        <Route path="servers" element={<ServersPage />} />
        <Route path="channels" element={<ChannelsPage />} />
        <Route path="channels/:slug" element={<ChannelDetailRoute />} />
        <Route path="channels/:slug/playout" element={<PlayoutRoute />} />
        <Route path="channels/:slug/schedule" element={<ScheduleRoute />} />
        <Route path="channels/:slug/podcast" element={<PodcastRoute />} />
        <Route path="channels/:slug/recordings" element={<PodcastRoute />} />
        <Route path="frage-der-woche" element={<FrageDerWochePage />} />
        <Route path="telefon" element={<PhonePage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="kalender" element={<EventsPage />} />
        <Route path="mitschnitt" element={<MitschnittPage />} />
        {/* Ans Ende gehängt, damit parallele Zweige sich hier nicht in die Quere kommen. */}
        <Route path="veranstaltungen" element={<VeranstaltungPage />} />
        {/* Werbeplanung (f-werbung) */}
        <Route path="werbung" element={<WerbungPage />} />
        {/* Kanalseiten (f-web) */}
        <Route path="channels/:slug/hoererseite" element={<KanalHoererseiteRoute />} />
        {/* Hosting (h-ui) */}
        <Route path="tarif" element={<MeinTarifPage />} />
        {/* h-domain */}
        <Route path="adresse" element={<EigeneAdressePage />} />
        {/* h-umzug */}
        <Route path="betrieb/import" element={<ImportPage />} />
        <Route path="betrieb/loeschnachweise" element={<LoeschnachweisePage />} />
        <Route path="meine-daten" element={<MeineDatenPage />} />
        <Route path="betrieb/radios" element={<RadiosPage />} />
        <Route path="betrieb/radios/:id" element={<RadioDetailRoute />} />
        <Route path="betrieb/tarife" element={<TarifePage />} />
        <Route path="betrieb/nutzung" element={<NutzungPage />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}
